import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ShieldCheck, Building2, Layers, LogOut, Wrench, Settings, Home, Globe, ArrowLeftRight, Tag } from "lucide-react";

// iter309-redesign PR 2: primary nav reduced to 5 items per the
// super-admin control-plane redesign brief. Removed as standalone
// primary nav items (still reachable via detail pages or direct URLs):
//   - Tenant Users     → merged into Tenants → Tenant Detail → Users tab
//   - Billing Emails   → will move under Maintenance in PR 6
//   - Cron Ledger      → will move under Maintenance in PR 6
//   - Audit Log        → will move under Maintenance in PR 6
//   - User Volumes     → surfaced as aggregate cards on Platform Overview
const NAV = [
  { to: "/super-admin/overview", label: "Platform Overview", icon: Home },
  { to: "/super-admin/tenants", label: "Tenants", icon: Building2 },
  { to: "/super-admin/plans", label: "Plans", icon: Layers },
  { to: "/super-admin/features", label: "Features & Pricing", icon: Tag },
  { to: "/super-admin/plan-requests", label: "Plan Requests", icon: ArrowLeftRight },
  { to: "/super-admin/website", label: "Website", icon: Globe },
  { to: "/super-admin/settings", label: "Platform Settings", icon: Settings },
  { to: "/super-admin/maintenance", label: "Maintenance", icon: Wrench },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'robots'); document.head.appendChild(m); }
    m.setAttribute('content', 'noindex, nofollow');
  }, []);
  const handleLogout = async () => {
    try { await logout(); } catch (_) { /* logout handles errors */ }
    navigate("/", { replace: true });
  };

  // iter318 wave 21 — Super-admin console lives at www.vyomos.io/super-admin.
  // If the operator lands here on a tenant subdomain (legacy deep links),
  // send them to the canonical platform host to avoid the "why does the
  // super-admin URL say dascricket?" confusion.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname.toLowerCase();
    const isPlatformHost =
      host === "www.vyomos.io" || host === "vyomos.io" ||
      host.endsWith(".localhost") || host === "localhost";
    if (isPlatformHost) return;
    // Preview host (emergentagent.com etc.) — stay put so devs can test.
    if (!host.endsWith(".vyomos.io")) return;
    const target = `https://www.vyomos.io${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
  }, []);

  if (!user) return <Navigate to="/" replace />;
  if (user.role !== "super_admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md" data-testid="super-admin-forbidden">
          <ShieldCheck className="w-10 h-10 text-amber-500 mb-3" />
          <h2 className="text-xl font-semibold mb-1">Super-admin access only</h2>
          <p className="text-sm text-slate-600">
            You are signed in as <span className="font-mono">{user.email}</span> ({user.role}).
            This area is reserved for platform owners.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-60 bg-slate-900 text-slate-100 p-4 flex flex-col" data-testid="super-admin-sidebar">
        <div className="flex items-center gap-2 mb-6 px-2">
          <ShieldCheck className="w-5 h-5 text-amber-400" />
          <span className="font-semibold tracking-tight">Super Admin</span>
        </div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`super-admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 px-2 space-y-3">
          <div className="text-xs text-slate-400">
            Logged in as
            <div className="text-slate-200 truncate">{user.email}</div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="super-admin-logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-sm bg-slate-800 hover:bg-rose-600 text-slate-200 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
